import { Injectable } from "@angular/core";
import
{
  ShopProductFamily,
  ShopProduct,
  Order,
  Cart,
  Wishlist
} from "@dto";
import
{
  SearchResult,
  SearchQuery,
} from "@idto";
import { NavigationEnd } from "@angular/router";
import { AnalyticsBaseService } from "./abstract/analytics-base.service";
import { environment } from "environments/environment";
import { ShopModeService } from "@service/helpers/shop-mode.service";

@Injectable({
  providedIn: 'root',
})
export class RtbHouseAnalyticsService extends AnalyticsBaseService
{
  private uid: string = 'unknown';

  // Ostatni tag "strony" odrzucony z braku zgody — wysyłany raz, zaraz po jej
  // udzieleniu, żeby tagowanie ruszyło od bieżącej strony bez przeładowania
  // (wymóg RTB House: tagi natychmiast po zgodzie, nie po reloadzie).
  private pendingPageEvent: { [key: string]: any } | null = null;

  // Eventy akcji (basketadd/wishlist/…) z okna, w którym CMP jeszcze się nie
  // załadował — u powracającego użytkownika ze zgodą z poprzedniej sesji
  // przepadałyby mimo ważnej zgody. Po załadowaniu CMP: zgoda → flush,
  // brak zgody → drop (stan CMP jest autorytatywny).
  private pendingActionEvents: { [key: string]: any }[] = [];
  private static readonly MAX_PENDING_ACTION_EVENTS = 20;

  private initEventQueued: boolean = false;

  // Limit prób wstawienia skryptu — bez niego adblock na creativecdn oznaczałby
  // jedną martwą próbę sieciową na każdy event do końca sesji.
  private scriptLoadAttempts: number = 0;
  private static readonly MAX_SCRIPT_LOAD_ATTEMPTS = 3;

  constructor(private shopModeService: ShopModeService) { super(); }

  public getName(): string
  {
    return 'RtbHouseAnalyticsService';
  }

  public isEnabled(): boolean
  {
    return (typeof (window as any).rtbhEvents !== 'undefined');
  }

  // Zgoda jest egzekwowana per-event w push() — initialize() NIE blokuje kolejki
  // agregatora. Dzięki temu eventy sprzed zgody są odrzucane na bieżąco, a nie
  // odtwarzane hurtem po jej udzieleniu; cofnięcie zgody zatrzymuje wysyłkę od
  // następnego eventu.
  public async initialize(): Promise<void>
  {
    if (this.isTaggingDisabled())
    {
      return;
    }

    // Listenery zostają na stałe: obsługują też ponowne udzielenie zgody po jej
    // wcześniejszym cofnięciu. Decyzja zawsze pochodzi z currentState(), listener
    // jest tylko wyzwalaczem (CookieScript emituje CookieScriptAccept również
    // przy zapisie ustawień z odznaczonym marketingiem — wtedy stan to 'reject').
    const consentEvents = ['CookieScriptLoaded', 'CookieScriptAccept', 'CookieScriptAcceptAll'];
    const listener = () =>
    {
      if (this.hasMarketingConsent())
      {
        this.loadScript();

        if (this.pendingPageEvent)
        {
          const pageEvent = this.pendingPageEvent;
          this.pendingPageEvent = null;
          this.push(pageEvent, true);
        }

        const actionEvents = this.pendingActionEvents;
        this.pendingActionEvents = [];
        actionEvents.forEach(actionEvent => this.push(actionEvent));
      }
      else if ((window as any).CookieScript != null)
      {
        // CMP obecny i mówi "brak zgody" — bufor z okna przed jego
        // załadowaniem przepada.
        this.pendingActionEvents = [];
      }
    };

    consentEvents.forEach(eventName => window.addEventListener(eventName, listener));

    if (this.hasMarketingConsent())
    {
      this.loadScript();
    }
  }

  // Twarda allow-lista: zgoda marketingowa wyłącznie przy action 'accept'
  // z jawną kategorią 'targeting'. Żadnych furtek fail-open — kształt
  // categories zależy od zdalnej konfiguracji CookieScript i może się zmienić
  // bez deployu. Uwaga: window.CookieScript wstrzykuje GoogleAdsAnalyticsService
  // (loadAdditionalScript po onload gtag) — wyłączenie googleAdsAnalytics
  // zostawia RTB bez CMP, czyli bez zgody i bez tagów.
  private hasMarketingConsent(): boolean
  {
    try
    {
      const state = (window as any).CookieScript?.instance?.currentState?.();

      return state != null
        && state.action === 'accept'
        && Array.isArray(state.categories)
        && state.categories.indexOf('targeting') >= 0;
    }
    catch
    {
      // Kształt API CookieScript zależy od zdalnej konfiguracji — egzotyczny
      // stan ma odmawiać zgody, nie blokować kolejki retry'ami.
      return false;
    }
  }

  // Kiosk salonowy to współdzielony terminal — remarketing nie ma tam odbiorcy,
  // a hash e-maila jednego klienta mógłby skleić się z aktywnością następnego.
  // Ruggo to osobna marka na osobnej domenie — hash tagowania jest wystawiony
  // dla arte.pl i tagowałby cudzy ruch kontem arte.
  private isTaggingDisabled(): boolean
  {
    return this.shopModeService.isKioskMode() || this.shopModeService.isRuggoMode();
  }

  private loadScript(): void
  {
    const taggingHash = environment.analytics.rtbHouseTaggingHash;

    // "?" to placeholder rynków bez konta RTB (konwencja jak bingTagID w cz) —
    // bez guarda flip flagi generowałby request do tags.creativecdn.com/?.js
    // przy każdym evencie.
    if (!taggingHash || taggingHash === '?')
    {
      return;
    }

    if (this.scriptLoadAttempts >= RtbHouseAnalyticsService.MAX_SCRIPT_LOAD_ATTEMPTS)
    {
      return;
    }

    if ((window as any).rtbhScriptPromise == null)
    {
      this.scriptLoadAttempts++;
      (window as any).rtbhScriptPromise = new Promise<void>((resolve) =>
      {
        (window as any).rtbhEvents = (window as any).rtbhEvents || [];

        if (!this.initEventQueued)
        {
          this.initEventQueued = true;
          (window as any).rtbhEvents.push({ eventType: 'init', value: taggingHash, dc: 'ams' });
        }

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.onload = () =>
        {
          resolve();
        }
        script.onerror = () =>
        {
          // Wyczyszczenie cache pozwala kolejnemu push() ponowić wstawienie
          // skryptu po chwilowym błędzie sieci (bez tego jeden fail zabijałby
          // tag do końca sesji). Brak reject — nikt nie awaituje tej obietnicy.
          script.remove();
          (window as any).rtbhScriptPromise = null;
        }
        script.src = 'https://tags.creativecdn.com/' + taggingHash + '.js';

        document.getElementsByTagName('body')[0].appendChild(script);
      });
    }
  }

  private push(event: { [key: string]: any }, isPageEvent: boolean = false): void
  {
    if (this.isTaggingDisabled())
    {
      return;
    }

    if (!this.hasMarketingConsent())
    {
      if (isPageEvent)
      {
        this.pendingPageEvent = event;
      }
      else if ((window as any).CookieScript == null
        && this.pendingActionEvents.length < RtbHouseAnalyticsService.MAX_PENDING_ACTION_EVENTS)
      {
        this.pendingActionEvents.push(event);
      }

      return;
    }

    this.loadScript();

    if ((window as any).rtbhScriptPromise == null)
    {
      // Skrypt nie może się załadować (placeholder hash / wyczerpane próby) —
      // nie zapełniamy tablicy, której nikt nie skonsumuje.
      return;
    }

    (window as any).rtbhEvents = (window as any).rtbhEvents || [];
    // Jedno wywołanie push z parą event+uid — dokładnie jak w oficjalnym
    // snippecie z panelu RTB (tag przetwarza oba obiekty w jednym cyklu).
    (window as any).rtbhEvents.push(event, { eventType: 'uid', id: this.uid });
  }

  //#region Generic events
  public async registerPageView(event: NavigationEnd): Promise<void>
  {
    const path = event.urlAfterRedirects.split(/[?#]/)[0];

    if (path == "/")
    {
      this.push({ eventType: 'home' }, true);
      return;
    }

    // Placebo sitewide — instrukcja RTB dopuszcza je "across all pages", co
    // zdejmuje kruche wykluczenia po routingu (pusty koszyk, podstrony
    // checkoutu, PDP 404, wyszukiwanie bez wyników zostają otagowane).
    this.push({ eventType: 'placebo' }, true);
  }
  //#endregion

  //#region Shop flow events
  public async registerProductListView(result: SearchResult, list: string): Promise<void>
  {
    if (!result || !result.records)
    {
      return;
    }

    const searchQuery = result.searchQuery;

    if (searchQuery && searchQuery.query && searchQuery.query !== "*")
    {
      // Wyniki wyszukiwania — max 5 unikalnych item group ID, per instrukcja
      // RTB House (dedup przed slice, żeby duplikaty nie zjadały limitu).
      const ids = result.records
        .map(item => item.productGroupID)
        .filter(id => !!id)
        .map(id => String(id))
        .filter((id, index, all) => all.indexOf(id) === index)
        .slice(0, 5);

      if (ids.length === 0)
      {
        return;
      }

      this.push({ eventType: 'listing', offerIds: ids }, true);
    }
    else
    {
      const categoryName = this.getCategoryName(searchQuery);

      if (!categoryName)
      {
        return;
      }

      this.push({ eventType: 'category', categoryName: categoryName }, true);
    }
  }

  // Opisowa nazwa kategorii (np. 'dywany/nowoczesne') zamiast surowego query
  // stringa wyszukiwarki — uRL zawiera paginację/sortowanie, co eksplodowałoby
  // kardynalność kategorii po stronie RTB.
  private getCategoryName(searchQuery: SearchQuery): string
  {
    if (!searchQuery)
    {
      return '';
    }

    if (searchQuery.friendlyURL)
    {
      const path = searchQuery.friendlyURL.split(/[?#]/)[0];

      try
      {
        return decodeURIComponent(path).replace(/^\/+|\/+$/g, '');
      }
      catch
      {
        return path.replace(/^\/+|\/+$/g, '');
      }
    }

    // Brak friendlyURL = filtrowany/paginowany listing, gdzie uRL to surowy
    // query string — jako categoryName eksplodowałby kardynalność kategorii.
    // Strona jest i tak pokryta przez placebo z registerPageView.
    return '';
  }

  public async registerProductPageView(product: ShopProductFamily): Promise<void>
  {
    if (!product || !product.familyCode)
    {
      return;
    }

    this.push({ eventType: 'offer', offerId: String(product.familyCode) }, true);
  }

  public async registerWishlistAdd(wishlist: Wishlist, profileFamilySku: string): Promise<void>
  {
    if (!profileFamilySku)
    {
      return;
    }

    this.push({ eventType: 'wishlist', offerId: String(profileFamilySku) });
  }
  //#endregion

  //#region Transaction
  public async registerCartAdd(product: ShopProduct, quantity: number, cart: Cart): Promise<void>
  {
    if (!product || !product.familyCode)
    {
      return;
    }

    this.push({ eventType: 'basketadd', offerId: String(product.familyCode) });
  }

  public async registerCartView(cart: Cart): Promise<void>
  {
    this.pushBasketStatus(cart, true);
  }

  public async registerCartRemove(product: ShopProduct, quantity: number, cart: Cart): Promise<void>
  {
    this.pushBasketStatus(cart, false);
  }

  // Instrukcja RTB: basketstatus tylko gdy koszyk ma >= 1 pozycję,
  // unikalne ID niezależnie od ilości.
  private pushBasketStatus(cart: Cart, isPageEvent: boolean): void
  {
    const ids = this.collectOfferIds(cart ? cart.items : null, true);

    if (ids.length === 0)
    {
      return;
    }

    this.push({ eventType: 'basketstatus', offerIds: ids }, isPageEvent);
  }

  public async registerTransaction(order: Order): Promise<void>
  {
    if (!order || order.iD == null)
    {
      return;
    }

    const ids = this.collectOfferIds(order.items, false);

    this.push({
      eventType: 'conversion',
      conversionClass: 'order',
      conversionSubClass: 'purchase',
      conversionId: String(order.iD),
      offerIds: ids,
      conversionValue: ((order.productsTotalBrutto || 0) / 1.23).toFixed(2),
      conversionCurrency: 'PLN'
    });
  }

  private collectOfferIds(items: any[], onlyActiveItems: boolean): string[]
  {
    const allItems = [];

    (items || []).forEach(item =>
    {
      if (!item || item.deleted === true)
      {
        return;
      }

      // W koszyku pozycje odznaczone checkboxem (active === false) nie są
      // częścią intencji zakupowej; w zamówieniu pola active nie ma.
      if (onlyActiveItems && item.active === false)
      {
        return;
      }

      allItems.push(item);

      (item.subItems || []).forEach(subItem =>
      {
        if (!subItem || subItem.deleted === true)
        {
          return;
        }

        if (!onlyActiveItems || subItem.active)
        {
          allItems.push(subItem);
        }
      });
    });

    return allItems
      .map(item => item && item.product ? item.product.familyCode : null)
      .filter(code => !!code)
      .map(code => String(code))
      .filter((id, index, all) => all.indexOf(id) === index);
  }
  //#endregion

  //#region Basket steps flow
  // startorder jest przywiązany do STRONY formularza danych (agregator routuje
  // po URL w registerPageView), nie do kliknięcia "przejdź do kasy" — dzięki
  // temu bezpośrednie wejście i reload też emitują tag. Tylko strona danych
  // (nie logowania), bo gość i zalogowany i tak przez nią przechodzą — jeden
  // startorder na przejście, zgodnie z instrukcją ("typowo formularz danych").
  public async registerCheckoutCustomerDataEnter(): Promise<void>
  {
    this.push({ eventType: 'startorder' }, true);
  }
  //#endregion

  //#region Customer
  public async registerUserData(email: string, phone: string): Promise<void>
  {
    if (!email)
    {
      // Reset wołany jawnie z logout(), login() i po rejestracji transakcji —
      // inaczej uid poprzedniego klienta wyciekałby na eventy następnej osoby
      // na współdzielonym komputerze.
      this.uid = 'unknown';
      return;
    }

    try
    {
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(email.trim().toLowerCase()));
      this.uid = Array.from(new Uint8Array(digest)).map(byte => byte.toString(16).padStart(2, '0')).join('');
    }
    catch
    {
      this.uid = 'unknown';
    }
  }
  //#endregion
}
